import { CUSTOM_FILTERS_SELECTOR, DATATABLES_SELECTOR, PJAX_MAIN_CONTAINER_ID, SCREEN_SM } from './constants';
import { debounce } from './eventDelaying';
import { dTAddHref, dTChainRenderers, dTRowCallback, renderers } from './lists';
import { store } from './reactTreeMounting';

const processing = '<i class="fa fa-spinner fa-spin dataTables_processing_loader"></i>';
const DATATABLES_TRANSLATIONS = {
  en: {
    sProcessing: processing,
  },
  fr: {
    sProcessing: processing,
    sSearch: 'Rechercher&nbsp;:',
    sLengthMenu: 'Afficher _MENU_ &eacute;l&eacute;ments',
    sInfo: 'Affichage de l’&eacute;l&eacute;ment _START_ &agrave; _END_ sur _TOTAL_ &eacute;l&eacute;ments',
    sInfoEmpty: 'Affichage de l’&eacute;l&eacute;ment 0 &agrave; 0 sur 0 &eacute;l&eacute;ments',
    sInfoFiltered: '(filtr&eacute; de _MAX_ &eacute;l&eacute;ments au total)',
    sInfoPostFix: '',
    sLoadingRecords: 'Chargement en cours...',
    sZeroRecords: 'Aucun &eacute;l&eacute;ment &agrave; afficher',
    sEmptyTable: 'Aucune donn&eacute;e disponible dans le tableau',
    oPaginate: {
      sFirst: 'Premier',
      sPrevious: 'Pr&eacute;c&eacute;dent',
      sNext: 'Suivant',
      sLast: 'Dernier',
    },
    oAria: {
      sSortAscending: ': activer pour trier la colonne par ordre croissant',
      sSortDescending: ': activer pour trier la colonne par ordre d&eacute;croissant',
    },
  },
};

$.fn.dataTable.ext.errMode = 'throw';

$(document).on('pjax:beforeCache pjax:beforeSend', function destroyJSElements() {
  /**
   * On PJAX navigation, destroy datatables so that they can be recreated when navigating back on this page.
   * If we don't do that, the datatables still exist in cache but all events have been destroyed, rendering the
   * tables pretty useless.
   */
  const table = $(DATATABLES_SELECTOR).addClass('hidden');
  if ($.fn.DataTable.isDataTable(table)) {
    table.DataTable().destroy();
  }

  // Destroy select2 to avoid similar problem when navigating back.
  $('select.select2-hidden-accessible').addClass('hidden').select2('destroy');
});

function getFilterStatus(state, filter) {
  const filterInput = filter.find('input');
  const filterCondition = filter.data('condition');
  const filterDefault = filter.data('default');

  if (!filterInput.length && (!state || !(filterCondition in state))) {
    // First load: no input and no saved state.
    // Check for a data-checked attribute written by the React component. This is necessary because
    // oLoadedState is null on the very first page visit (no prior localStorage entry), so mutations
    // to state.loaded() || {} are invisible to DataTables. React synchronously sets data-checked via
    // setAttribute before triggering draw(), so we can read the correct current value here.
    const checkedAttr = filter.attr('data-checked');
    if (checkedAttr !== undefined) {
      return checkedAttr === 'true' || checkedAttr === true;
    }
    return typeof filterDefault !== 'undefined' ? filterDefault : true;
  } else if (!filterInput.length) {
    // Before table is visible: input is not visible but table has a state => use the state.
    return state[filterCondition];
  } else {
    // Table is visible => use the input.
    return filterInput.is(':checked');
  }
}

function addCustomFiltersToAjaxRequest(data, settings) {
  /**
   * When doing datatables AJAX requests, add custom filters to the parameters.
   */
  // Using the settings is slightly faster than doing table.DataTable();
  const api = new $.fn.dataTable.Api(settings);
  const state = api.state.loaded();

  $(CUSTOM_FILTERS_SELECTOR).each(function addCustomFilterToAjax() {
    const filterCondition = $(this).data('condition');

    // Check state first, fallback to DOM if state doesn't exist
    let filterChecked;
    if (state && filterCondition in state) {
      filterChecked = state[filterCondition];
    } else {
      filterChecked = getFilterStatus(state, $(this));
    }

    const useFilter = !filterChecked; // When unchecked, apply the filter

    if (useFilter && !data.filter) {
      data.filter = filterCondition;
    } else if (useFilter) {
      data.filter += `&&${filterCondition}`;
    }
  });
}

function manageColumnsRender(table) {
  /**
   * Data rendering callback for datatables.
   */
  const columns = [];
  const customLinks = table.data('custom-links');

  // Loop through all the columns, to be able to hook the 'data-custom-render' parameters to existing functions.
  table.find('th').each(function setRenderFunctions() {
    const column = {
      label: $(this).text() || $(this).data('label'),
      name: $(this).data('data'),
    };
    // We can't set the render functions using HTML5 data parameters, so we simulate this behavior.
    const renderFunction = $(this).data('custom-render');
    if (customLinks && renderFunction) {
      column.render = renderers[renderFunction];
    } else if (!customLinks && renderFunction) {
      column.render = dTChainRenderers(renderFunction);
    } else if (!customLinks) {
      column.render = dTAddHref;
    }
    columns.push(column);
  });

  return columns;
}

function stateSaveParams(settings, data) {
  // Using the settings is slightly faster than doing table.DataTable();
  const api = new $.fn.dataTable.Api(settings);
  const state = api.state.loaded();

  $(CUSTOM_FILTERS_SELECTOR).each(function saveFilterState() {
    const filterCondition = $(this).data('condition');
    data[filterCondition] = getFilterStatus(state, $(this));
  });
}

function cancelColReorderEvents() {
  $(document).unbind('mousemove.ColReorder');
  $(document).unbind('mouseup.ColReorder');
  $(document).unbind('touchend.ColReorder');
  $(document).unbind('touchmove.ColReorder');
}

export function createDataTables() {
  /**
   * Create the dataTable.
   */
  const table = $(DATATABLES_SELECTOR);
  if (!table.length) {
    return;
  }

  const clientSide = Boolean(table.data('data'));
  const customFilters = $(CUSTOM_FILTERS_SELECTOR);

  const dataTableParameters = {
    // Documentation: https://datatables.net/reference/option/dom
    dom: '<tr><ip>',
    stateSave: true,
    pageLength: 50,
    lengthChange: false,
    lengthMenu: false,
    responsive: {
      details: false,
    },
    processing: true,
    columns: manageColumnsRender(table),
    rowCallback: dTRowCallback,
    drawCallback: function() {
      // Only show pager when we need to (more than 1 page).
      const pagination = $(this).closest('.dataTables_wrapper').find('.dataTables_paginate');
      pagination.toggle(this.api().page.info()?.pages > 1);
    },
    // Change pagination style on mobile to avoid display issues: https://datatables.net/reference/option/pagingType
    pagingType: $(window).width() >= 576 ? 'simple_numbers' : 'full',
    colReorder: true,
    colResize: {
      isEnabled: true,
      isResizable: function(column) {
        return !!column.label;
      },
      onResizeStart: cancelColReorderEvents,
      onResize: cancelColReorderEvents,
    },
  };

  // Offer the possibility to have client-side data.
  if (clientSide) {
    dataTableParameters.serverSide = false;
  } else {
    dataTableParameters.serverSide = true;
    dataTableParameters.ajax = {
      url: table.data('ajax-url'),
      // Prevent ajax errors to throw here. They are caught in init.js.
      error: () => {},
    };

    if (customFilters.length) {
      dataTableParameters.ajax.data = addCustomFiltersToAjaxRequest;
    }
  }

  const locale = store?.getState().user.locale || window.userLocale;
  if (locale in DATATABLES_TRANSLATIONS) {
    dataTableParameters.language = DATATABLES_TRANSLATIONS[locale];
  }

  if (customFilters.length) {
    dataTableParameters.stateSaveParams = stateSaveParams;
  }

  const initializedDataTable = table.DataTable(dataTableParameters);
  initializedDataTable.columnsSettings = dataTableParameters.columns;

  // If an ajax call is long, the user can browse before the response is received. As the state is by default
  // saved after reception of the response, the state changes can be lost.
  // To prevent this, save the state before making the ajax request.
  table.on('preXhr.dt', initializedDataTable.state.save);

  if ('ResizeObserver' in window) {
    const mainContainer = document.getElementById(PJAX_MAIN_CONTAINER_ID);
    const containerResizeObserver = new window.ResizeObserver(debounce(() => {
      if (mainContainer.offsetWidth < SCREEN_SM) {
        mainContainer.classList.add('screen_sm');
      } else {
        mainContainer.classList.remove('screen_sm');
      }
      initializedDataTable.columns.adjust();
    }, 200));
    containerResizeObserver.observe(mainContainer);
  }
  return initializedDataTable;
}
